'use client';

import { editUserDetails } from '@/services/userService';
import { setCookie } from 'cookies-next';
import { toast } from 'sonner';

import useLanguageConflictChecker from '@/hooks/useLanguageConflictChecker';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Language display names
const languageNames: Record<string, string> = {
  'en-US': 'English',
  'zh-CN': 'Chinese (Simplified)',
  'ja-JP': 'Japanese',
  'de-DE': 'German',
  'ko-KP': 'Korean',
  'uk-UA': 'Ukrainian',
};

interface LanguageConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  savedLanguage: string;
  currentLanguage: string;
  userId: string;
  accessToken: string;
  tenantId: string;
  tenantName: string;
  updateURL: URL;
}

const LanguageConfirmationDialog = ({
  isOpen,
  onClose,
  currentLanguage,
  userId,
  accessToken,
  tenantId,
  tenantName,
  updateURL,
  savedLanguage,
}: LanguageConfirmationDialogProps) => {
  const { isLoading } = useLanguageConflictChecker({
    accessToken,
    tenantId,
    tenantName,
  });

  const handleKeepCurrent = async () => {
    try {
      const updateValues = {
        language_preference: currentLanguage,
      };
      console.log({ userId, updateValues, tenantId, tenantName, accessToken });

      const response = await editUserDetails(
        userId,
        updateValues,
        tenantId,
        tenantName,
        accessToken
      );

      if (response.status === 201) {
        setCookie('NEXT_LOCALE', currentLanguage, {
          path: '/auth/ui',
        });
        setCookie('NEXT_LOCALE', currentLanguage, {
          path: '/',
        });

        toast.success(`Language set to ${languageNames[currentLanguage]}`);
        updateURL?.searchParams?.set('locale', currentLanguage);
        window.location.replace(updateURL);
      } else {
        toast.error('Failed to update language preference');
      }
    } catch (error) {
      console.error('Error updating language preference:', error);
      toast.error('Failed to update language preference');
    } finally {
      onClose();
    }
  };

  const handleIgnore = () => {
    onClose();
    setCookie('NEXT_LOCALE', savedLanguage, {
      path: '/auth/ui',
    });
    setCookie('NEXT_LOCALE', savedLanguage, {
      path: '/',
    });
    updateURL?.searchParams?.set('locale', savedLanguage);
    window.location.replace(updateURL);
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Language Preference</AlertDialogTitle>
          <AlertDialogDescription>
            Your saved language preference is different. Which language would
            you like to use and save for future translations?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="mt-3">
          <AlertDialogAction onClick={handleKeepCurrent} disabled={isLoading}>
            {languageNames[currentLanguage]} ({currentLanguage})
          </AlertDialogAction>
          <AlertDialogAction
            className="ml-3"
            onClick={handleIgnore}
            disabled={isLoading}
          >
            {languageNames[savedLanguage]} ({savedLanguage})
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default LanguageConfirmationDialog;
